import React, { useState, useEffect } from "react";
import { db } from "../firebase"; // Ensure this path is correct
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import "./SubscribersList.css";

// --- Main Subscribers List Component ---
const SubscribersList = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for dropdown data
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);

  // State for modal and form
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSubscriberId, setCurrentSubscriberId] = useState(null);
  const [formData, setFormData] = useState({
    customerName: "",
    customerEmail: "",
    packageName: "",
    pricePerMonth: "",
    status: "active",
  });

  const subscribersCollectionRef = collection(db, "user_subscriptions");

  // --- READ: Fetches all necessary data from Firestore ---
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch subscribers
      const subQuery = query(subscribersCollectionRef, orderBy("subscriptionStartDate", "desc"));
      const subSnapshot = await getDocs(subQuery);
      setSubscribers(subSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      // Fetch customers for the dropdown
      const customerCol = collection(db, "customer");
      const customerSnapshot = await getDocs(customerCol);
      setCustomers(customerSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      // Fetch packages for the dropdown (ensure your collection is named 'packages')
      const packageCol = collection(db, "packages");
      const packageSnapshot = await getDocs(packageCol);
      setPackages(packageSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      alert("Failed to fetch data. Please check the console for errors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Modal and Form Handlers ---
  const openModal = () => setIsModalOpen(true);
  
  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setCurrentSubscriberId(null);
    setFormData({ customerName: "", customerEmail: "", packageName: "", pricePerMonth: "", status: "active" });
  };

  // --- CREATE / UPDATE ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.customerEmail || !formData.packageName) {
      alert("Please ensure a customer and package are selected.");
      return;
    }

    const dataToSave = {
        ...formData,
        pricePerMonth: Number(formData.pricePerMonth) || 0
    };

    if (isEditing) {
      // UPDATE logic
      const subscriberDoc = doc(db, "user_subscriptions", currentSubscriberId);
      await updateDoc(subscriberDoc, dataToSave);
      alert("Subscriber updated successfully!");
    } else {
      // CREATE logic
      await addDoc(subscribersCollectionRef, {
        ...dataToSave,
        subscriptionStartDate: Timestamp.now(),
      });
      alert("Subscriber added successfully!");
    }
    
    fetchData(); // Refresh all data
    closeModal();
  };

  // --- EDIT: Pre-fills the form for editing ---
  const handleEdit = (subscriber) => {
    setIsEditing(true);
    setCurrentSubscriberId(subscriber.id);
    setFormData({
      customerName: subscriber.customerName,
      customerEmail: subscriber.customerEmail,
      packageName: subscriber.packageName,
      pricePerMonth: subscriber.pricePerMonth,
      status: subscriber.status,
    });
    openModal();
  };

  // --- DELETE: Removes a subscriber ---
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this subscriber?")) {
      const subscriberDoc = doc(db, "user_subscriptions", id);
      await deleteDoc(subscriberDoc);
      alert("Subscriber deleted successfully!");
      fetchData(); // Refresh data
    }
  };
  
  // --- Helper Functions ---
  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return "N/A";
    return timestamp.toDate().toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const StatusBadge = ({ status }) => {
    const statusClass = status ? status.toLowerCase() : 'active';
    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  if (loading) {
    return <div className="loading-container">Loading Subscribers...</div>;
  }

  return (
    <div className="subscribers-container">
      <div className="subscribers-header">
        <h1 className="subscribers-title">Subscribers Management</h1>
        <button className="add-button" onClick={openModal}>
          <FaPlus style={{ marginRight: "8px" }} /> Add Subscriber
        </button>
      </div>

      <div className="table-container">
        <table className="subscribers-table">
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Email</th>
              <th>Package</th>
              <th>Price/Month</th>
              <th>Start Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((sub) => (
              <tr key={sub.id}>
                <td data-label="Name">{sub.customerName}</td>
                <td data-label="Email">{sub.customerEmail}</td>
                <td data-label="Package">{sub.packageName}</td>
                <td data-label="Price">${sub.pricePerMonth}</td>
                <td data-label="Start Date">{formatDate(sub.subscriptionStartDate)}</td>
                <td data-label="Status"><StatusBadge status={sub.status} /></td>
                <td data-label="Actions">
                  <button className="action-button edit-button" onClick={() => handleEdit(sub)} title="Edit">
                    <FaEdit />
                  </button>
                  <button className="action-button delete-button" onClick={() => handleDelete(sub.id)} title="Delete">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <Modal
          closeModal={closeModal}
          handleSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          isEditing={isEditing}
          customers={customers}
          packages={packages}
        />
      )}
    </div>
  );
};

// --- Modal Component ---
const Modal = ({ closeModal, handleSubmit, formData, setFormData, isEditing, customers, packages }) => {

  const handleCustomerSelect = (e) => {
    const customerId = e.target.value;
    const selected = customers.find(c => c.id === customerId);
    setFormData(prev => ({
        ...prev,
        customerName: selected ? selected.name : "",
        customerEmail: selected ? selected.email : "",
    }));
  };

  const handlePackageSelect = (e) => {
    const packageId = e.target.value;
    const selected = packages.find(p => p.id === packageId);
    setFormData(prev => ({
        ...prev,
        packageName: selected ? selected.name : "",
        pricePerMonth: selected ? selected.pricePerMonth : "",
    }));
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{isEditing ? "Edit Subscriber" : "Add New Subscriber"}</h2>
        <form onSubmit={handleSubmit}>
          {isEditing ? (
            <>
              {/* Form for EDITING */}
              <input className="modal-input" type="text" name="customerName" value={formData.customerName} onChange={handleInputChange} placeholder="Customer Name" required />
              <input className="modal-input" type="email" name="customerEmail" value={formData.customerEmail} onChange={handleInputChange} placeholder="Email Address" required />
              <input className="modal-input" type="text" name="packageName" value={formData.packageName} onChange={handleInputChange} placeholder="Package Name" required />
              <input className="modal-input" type="number" name="pricePerMonth" value={formData.pricePerMonth} onChange={handleInputChange} placeholder="Price Per Month" required />
            </>
          ) : (
            <>
              {/* Form for ADDING */}
              <label className="modal-label">Customer Name</label>
              <select className="modal-input" onChange={handleCustomerSelect} defaultValue="" required>
                <option value="" disabled>Select a Customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </select>

              <label className="modal-label">Email Address</label>
              <input className="modal-input" type="email" value={formData.customerEmail} placeholder="Email auto-fills here" readOnly />

              <label className="modal-label">Package Name</label>
              <select className="modal-input" onChange={handlePackageSelect} defaultValue="" required>
                <option value="" disabled>Select a Package</option>
                {packages.map(pkg => (
                  <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                ))}
              </select>

              <label className="modal-label">Price Per Month</label>
              <input className="modal-input" type="number" value={formData.pricePerMonth} placeholder="Price auto-fills here" readOnly />
            </>
          )}

          {/* Common fields */}
          <label htmlFor="status" className="modal-label">Subscription Status</label>
          <select id="status" className="modal-input" name="status" value={formData.status} onChange={handleInputChange}>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
          </select>
          
          <div className="modal-actions">
            <button type="submit" className="modal-button submit-button">
              {isEditing ? "Save Changes" : "Add Subscriber"}
            </button>
            <button type="button" className="modal-button cancel-button" onClick={closeModal}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscribersList;